import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
// @mui material components
import Grid from '@mui/material/Grid';
import { Box, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import MDButton from 'components/MDButton';
// Images and icons
import { Visibility, VisibilityOff } from '@mui/icons-material';
import hyperwolfLogo from 'assets/images/hyperwolf.svg';
import authImg from 'assets/images/auth-bg.jpg';
// Utilities and helpers
import { isEmptyObject } from 'utilities/common';
import { resetPin } from '../../../redux/slices/authSlice';
import { setLayout } from '../../../redux/slices/materialUISlice';
import { showToastMsg } from '../../../components/distributionSetting/distributionManagement/addRegionKit';

function validatePasswords(passwordObj) {
  const errors = {};

  // Check if password field is empty
  if (!passwordObj.password) {
    errors.password = 'PIN is required';
  } else {
    // Check password strength
    const passwordRegex = /^\d{4}$/;
    if (!passwordRegex.test(passwordObj.password)) {
      errors.password = 'PIN must be of 4 digits';
    }
  }

  // Check if confirmPassword field is empty
  if (!passwordObj.confirmPassword) {
    errors.confirmPassword = 'Confirm PIN is required';
  } else if (passwordObj.password !== passwordObj.confirmPassword) {
    errors.confirmPassword = 'PINs do not match';
  }

  // Return errors object if there are any errors, otherwise return null
  return Object.keys(errors).length ? { error: errors } : {};
}

function ResetPincode() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { loading = false } = useSelector(state => state.auth);
  const [userData, setUserData] = useState({});
  const [errors, setErrors] = useState({});
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);

  const handleResetPin = async () => {
    const { error } = validatePasswords(userData);
    if (!isEmptyObject(error)) {
      setErrors({ ...error });
      return;
    }
    setErrors({});

    dispatch(resetPin({ pin: userData?.password, token })).unwrap().then((res) => {
      navigate('/login');
      showToastMsg(res?.message, 'success');
    }).catch((err) => {
      showToastMsg(err?.message, 'error');
    });
  };

  const onChangeHandler = (e) => {
    setUserData({ ...userData, [e.target.name]: e.target.value });
  };

  const handleClickShowPassword = () => setShowPin((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    dispatch(setLayout('singup'));
  }, [pathname]);

  return (
    <Grid className="auth-pg" alignItems="center" container>
      <Grid item xs={12} md={6} p={2.5} pt={{ xs: 8, md: 2.5 }}>
        <Stack textAlign="center" direction="column" alignItems="center" justifyContent="center" maxWidth="500px" mx="auto" height="100%">
          <Link to='/login'>
            <img src={hyperwolfLogo} alt="HYPERWOLF" height="32px" />
          </Link>

          <Box textAlign="center" mt={6} mb={4}>
            <Typography variant="h2" fontWeight="semibold">Reset Pincode</Typography>
          </Box>

          <TextField sx={{ mb: 3 }}
            error={Boolean(errors?.password)}
            helperText={errors?.password}
            fullWidth
            label="Enter PIN"
            onChange={onChangeHandler}
            type={showPin ? 'text' : 'password'}
            name="password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="center">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="center"
                  >
                    {!showPin ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            error={Boolean(errors?.confirmPassword)}
            helperText={errors?.confirmPassword}
            fullWidth
            label="Confirm PIN"
            onChange={onChangeHandler}
            type={showConfirmPin ? 'text' : 'password'}
            name="confirmPassword"
            InputProps={{
              endAdornment: (
                <InputAdornment position="center">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowConfirmPin((showConfirmPassword) => !showConfirmPassword)}
                    onMouseDown={handleMouseDownPassword}
                    edge="center"
                  >
                    {!showConfirmPin ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <MDButton onClick={handleResetPin} sx={{ mt: 3, minWidth: '100%' }} disabled={loading} variant="contained" color="primary">Submit</MDButton>
        </Stack>
      </Grid>

      <Grid item xs={12} md={6} p={2.5}>
        <img src={authImg} alt="HYPERWOLF" className="auth-banner-img" />
      </Grid>
    </Grid>
  );
}

export default ResetPincode;
